import { Dispatch, RefObject, useEffect, useRef } from 'react';
import { TerraDraw } from 'terra-draw';
import type { OnFinishContext } from 'terra-draw/dist/common';
import type { FeatureId } from 'terra-draw/dist/store/store';

import {
  Action,
  DrawingActionKind,
  Overlay,
  Snapshot,
  State,
  isRectangle,
  isTerraDrawFeature,
} from './types';

interface ExtRegion {
  southwest: {
    latitude: number;
    longitude: number;
  };
  northeast: {
    latitude: number;
    longitude: number;
  };
}

const drawReducer = (state: State, action: Action) => {
  switch (action.type) {
    // This action is called whenever anything changes on any overlay.
    // We then take a snapshot of the relevant values of each overlay and
    // save them as the new "now". The old "now" is added to the "past" stack
    case DrawingActionKind.UPDATE_OVERLAYS: {
      const overlays = state.now.map((overlay: Overlay) => {
        const snapshot: Snapshot = {};
        const { geometry } = overlay;

        if (isRectangle(geometry)) {
          snapshot.bounds = geometry.getBounds()?.toJSON();
        } else if (isTerraDrawFeature(geometry)) {
          snapshot.coordinates = geometry.geometry.coordinates as number[][];
          snapshot.featureId = String(geometry.id);
        }

        return {
          ...overlay,
          snapshot,
        };
      });
      return {
        now: [...overlays],
        past: [...state.past, state.now],
        future: [],
      };
    }

    // This action is called when a new overlay is added to the map.
    // We then take a snapshot of the relevant values of the new overlay and
    // add it to the "now" state. The old "now" is added to the "past" stack
    case DrawingActionKind.SET_OVERLAY: {
      const { overlay, feature } = action.payload;

      const snapshot: Snapshot = {};

      if (isRectangle(overlay)) {
        snapshot.bounds = overlay.getBounds()?.toJSON();
        snapshot.fillColor = '#FF0000';
      } else if (feature && isTerraDrawFeature(feature)) {
        snapshot.coordinates = feature.geometry.coordinates as number[][];
        snapshot.fillColor = '#FF0000';
        snapshot.fillOpacity = 0.3;
        snapshot.featureId = String(feature.id);
      }

      const update = {
        past: [...state.past, state.now],
        now: [
          ...state.now,
          {
            type: action.payload.type,
            geometry: feature || action.payload.overlay,
            snapshot,
            featureId: feature?.id,
          },
        ],
        future: [],
      };
      return update;
    }

    case DrawingActionKind.REMOVE_OVERLAY: {
      const { overlay, feature } = action.payload;
      const updatedOverlays = state.now.filter((o) => {
        if (isRectangle(o.geometry) && isRectangle(overlay)) {
          return o.geometry.getBounds() !== overlay.getBounds();
        } else if (isTerraDrawFeature(o.geometry) && feature) {
          return o.featureId !== feature.id;
        }
        return true;
      });
      const update = {
        past: state.now ? [...state.past, state.now] : state.past,
        now: updatedOverlays,
        future: [],
      };
      return update;
    }

    // This action is called when the undo button is clicked.
    // Get the top item from the "past" stack and set it as the new "now".
    // Add the old "now" to the "future" stack to enable redo functionality
    case DrawingActionKind.UNDO: {
      const last = state.past.slice(-1)[0];

      if (!last) return state;
      const update = {
        past: [...state.past].slice(0, -1),
        now: last,
        future: state.now ? [...state.future, state.now] : state.future,
      };
      return update;
    }

    // This action is called when the redo button is clicked.
    // Get the top item from the "future" stack and set it as the new "now".
    // Add the old "now" to the "past" stack to enable undo functionality
    case DrawingActionKind.REDO: {
      const next = state.future.slice(-1)[0];

      if (!next) return state;

      const update = {
        past: state.now ? [...state.past, state.now] : state.past,
        now: next,
        future: [...state.future].slice(0, -1),
      };
      return update;
    }

    case DrawingActionKind.CLEAR: {
      return { past: [], now: [], future: [] }; // Clear all overlays
    }

    case DrawingActionKind.INITIALIZE_OVERLAYS: {
      const overlays = action.payload as Overlay[];
      return {
        past: [],
        now: overlays,
        future: [],
      };
    }
  }
};

export default function reducer(
  state: State,
  action: Action,
  updateRegions: (regions: Overlay[]) => void
) {
  return drawReducer(state, action);
}

// Handle Terra Draw events
export function useTerraDrawEvents(
  draw: TerraDraw | null,
  allowEmptyUpdateRef: RefObject<boolean>,
  dispatch: Dispatch<Action>
) {
  useEffect(() => {
    if (!draw) return;

    const handleFinish = (id: FeatureId, context: OnFinishContext) => {
      if (context.action !== 'draw') return;
      if (draw.getMode() !== 'rectangle') return;

      const feature = draw.getSnapshotFeature(id);
      if (!feature) return;
      if (feature.geometry.type !== 'Polygon') return;
      if (feature.properties?.mode !== 'rectangle') return;

      dispatch({
        type: DrawingActionKind.SET_OVERLAY,
        payload: { type: 'rectangle', overlay: feature, feature },
      });
    };

    const handleSelect = (id: FeatureId) => {
      if (draw.getMode() === 'rectangle') return;

      const feature = draw.getSnapshotFeature(id);
      if (!feature) return;
      if (feature.geometry.type !== 'Polygon') return;
      if (feature.properties?.mode !== 'rectangle') return;

      allowEmptyUpdateRef.current = true;
      draw.removeFeatures([id]);
      dispatch({
        type: DrawingActionKind.REMOVE_OVERLAY,
        payload: { type: 'rectangle', overlay: feature, feature },
      });
    };

    draw.on('finish', handleFinish);
    draw.on('select', handleSelect);

    return () => {
      draw.off('finish', handleFinish);
      draw.off('select', handleSelect);
    };
  }, [allowEmptyUpdateRef, dispatch, draw]);
}

export function useTerraDrawSnapshots(
  draw: TerraDraw | null,
  extRegions: ExtRegion[] | undefined,
  profileId: string | null,
  dispatch: Dispatch<Action>
) {
  const lastProfileIdRef = useRef<string | null>(null);
  const hasLoadedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!draw) return;

    if (profileId !== lastProfileIdRef.current) {
      lastProfileIdRef.current = profileId;
      hasLoadedRef.current = false;
    }

    if (hasLoadedRef.current) return;

    if (!extRegions || extRegions.length === 0) {
      hasLoadedRef.current = true;
      return;
    }

    // NOTE: The saved regions are stored as rectangles (southwest/northeast bounds)
    // We add them as rectangle features so Select mode can select/delete them.

    // Generate a UUID4 ID
    const generateUUID4 = (): string => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    };

    // Round coordinates to 9 decimal places (must match coordinatePrecision in useTerraDrawManager)
    const roundCoordinate = (coord: number): number => {
      return Math.round(coord * 1e9) / 1e9;
    };

    // Convert saved regions to TerraDraw rectangle features
    // Rectangle geometry in GeoJSON is represented as a Polygon with 5 coordinates
    // (start point, three corners, back to start point)
    // Important: TerraDraw requires feature IDs to be in UUID4 format and coordinates to match coordinator precision
    const features = extRegions.map((region: ExtRegion) => ({
      id: generateUUID4(),
      type: 'Feature' as const,
      geometry: {
        type: 'Polygon' as const,
        coordinates: [
          [
            [
              roundCoordinate(region.southwest.longitude),
              roundCoordinate(region.southwest.latitude),
            ],
            [
              roundCoordinate(region.northeast.longitude),
              roundCoordinate(region.southwest.latitude),
            ],
            [
              roundCoordinate(region.northeast.longitude),
              roundCoordinate(region.northeast.latitude),
            ],
            [
              roundCoordinate(region.southwest.longitude),
              roundCoordinate(region.northeast.latitude),
            ],
            [
              roundCoordinate(region.southwest.longitude),
              roundCoordinate(region.southwest.latitude),
            ],
          ],
        ],
      },
      properties: {
        mode: 'rectangle',
        source: 'saved',
      },
    }));

    // Add features to TerraDraw with a small delay to ensure it's fully ready
    const timeoutId = setTimeout(() => {
      if (features.length > 0) {
        try {
          // Log current draw state before adding
          const existingFeatures = draw.getSnapshot();
          console.log('TerraDraw mode before adding:', draw.getMode());
          console.log('Existing features before add:', existingFeatures.length);

          const existingSaved = draw
            .getSnapshot()
            .filter((feature) => feature.properties?.source === 'saved')
            .map((feature) => feature.id)
            .filter((id): id is FeatureId => id !== undefined);
          if (existingSaved.length > 0) {
            draw.removeFeatures(existingSaved);
          }

          const results = draw.addFeatures(features);

          // Check validation results
          results.forEach((result, idx) => {
            if (!result.valid) {
              console.error(`Feature ${idx} validation failed:`, result.reason);
            } else {
              console.log(`Feature ${idx} added successfully`);
            }
          });

          // Log state after adding
          const afterFeatures = draw.getSnapshot();
          console.log('Added saved regions to TerraDraw:', features.length);
          console.log('Existing features after add:', afterFeatures.length);

          const overlays: Overlay[] = features.map((feature) => ({
            type: 'rectangle',
            geometry: feature,
            snapshot: {
              coordinates: feature.geometry
                .coordinates as unknown as number[][],
              fillColor: '#FF0000',
              fillOpacity: 0.3,
              featureId: String(feature.id),
            },
            featureId: feature.id,
          }));
          dispatch({
            type: DrawingActionKind.INITIALIZE_OVERLAYS,
            payload: overlays,
          });
          hasLoadedRef.current = true;
        } catch (error) {
          console.error('Failed to add saved regions to TerraDraw:', error);
        }
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [dispatch, draw, extRegions, profileId]);
}
